import * as React from 'react';
import _ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
// @ts-ignore
import remarkEmoji from '@fec/remark-a11y-emoji';
import { useMemo } from 'react';
import { stripIndent } from '@epihub/core/src/fn/string';
import { styled } from '../../theme/theme';
import { MessageTypeMarkdownLink } from '../chat/MessageTypeMarkdownLink';
import { MessageTypeMarkdownImage } from '../chat/MessageTypeMarkdownImage';

// tsc complains randomly because react-markdown is typed badly
const ReactMarkdown = _ReactMarkdown as any;

type Props = {
  content: string;
  className?: string;
};

const remarkPlugins = [
  remarkGfm,
  remarkEmoji,
];

const components = {
  a: MessageTypeMarkdownLink,
  img: MessageTypeMarkdownImage,
};

export const MessageTypeMarkdown = ({
  content,
  className,
}: Props) => {
  const dedentedContent = useMemo(() => stripIndent(content), [content]);
  return (
    <Root className={className}>
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        components={components}
      >
        {dedentedContent}
      </ReactMarkdown>
    </Root>
  );
};

const Root = styled.div`
  font-size: 15px;
  line-height: 1.6;

  h1, h2, h3, h4, h5, h6 {
    margin-top: 1.5em;
    margin-bottom: 0.5em;
    font-weight: 600;
    line-height: 1.3;

    &:first-child {
      margin-top: 0;
    }
  }

  h1 {
    font-size: 1.75em;
  }

  h2 {
    font-size: 1.5em;
  }

  h3 {
    font-size: 1.25em;
  }

  h4 {
    font-size: 1.1em;
  }

  h5 {
    font-size: 1em;
  }

  h6 {
    font-size: 0.9em;
    color: var(--mantine-color-dimmed);
  }

  p {
    margin-top: 0;
    margin-bottom: 1em;

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul, ol {
    margin-top: 0;
    margin-bottom: 1em;
    padding-left: 1.5em;

    &:last-child {
      margin-bottom: 0;
    }
  }

  li {
    margin-bottom: 0.25em;

    &:last-child {
      margin-bottom: 0;
    }
  }

  blockquote {
    margin: 1em 0;
    padding: 0.5em 1em;
    border-left: 4px solid var(--mantine-color-default-border);
    background: var(--mantine-color-default-hover);

    &:last-child {
      margin-bottom: 0;
    }

    p:last-child {
      margin-bottom: 0;
    }
  }

  code {
    font-family: var(--mantine-font-family-monospace);
    font-size: 0.9em;
    padding: 0.15em 0.4em;
    background: var(--mantine-color-default-hover);
    border-radius: 4px;
  }

  pre {
    margin: 1em 0;
    padding: 1em;
    background: var(--mantine-color-default-hover);
    border-radius: 6px;
    overflow-x: auto;

    &:last-child {
      margin-bottom: 0;
    }

    code {
      padding: 0;
      background: none;
      border-radius: 0;
    }
  }

  strong {
    font-weight: 600;
  }

  em {
    font-style: italic;
  }

  hr {
    margin: 1.5em 0;
    border: none;
    border-top: 1px solid var(--mantine-color-default-border);
  }

  table {
    width: 100%;
    margin: 1em 0;
    border-collapse: collapse;

    &:last-child {
      margin-bottom: 0;
    }
  }

  th, td {
    padding: 0.5em 0.75em;
    border: 1px solid var(--mantine-color-default-border);
    text-align: left;
  }

  th {
    font-weight: 600;
    background: var(--mantine-color-default-hover);
  }
`;
